import React, { useState, useEffect } from 'react';
import { X, FileText, Globe, Download } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import './InvoiceModal.css';

const InvoiceModal = ({
  isOpen,
  onClose,
  onGenerate,
  session,
  mode = 'single',
  supplierName = '',
  suppliers = null,
  activeSupplierId = null,
  onSupplierChange = null,
  versionOptions = [],
  closeOnGenerate = true,
}) => {
  const { t } = useTranslation();

  const extractName = (nameWithRole) => {
    if (!nameWithRole) return '';
    // Format from backend: "FirstName LastName (Role)" → extract just the name
    const match = nameWithRole.match(/^(.+?)\s*\(.*\)$/);
    return match ? match[1].trim() : nameWithRole.trim();
  };

  const getDefaultParams = () => {
    const preparedBy = extractName(session?.trainer_name || '');
    const approvedBy = extractName(session?.validated_by_name || '');
    return {
      lang: 'pt',
      prepared_by: preparedBy,
      revised_by: '',
      approved_by: approvedBy,
      document_number: '',
      issue_date: new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }),
      revision_number: '',
      revision_date: '',
      service_type: '',
      requisition_number: '',
    };
  };

  const [params, setParams] = useState(getDefaultParams);
  const [selectedVersionKey, setSelectedVersionKey] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setParams(getDefaultParams());
      setSelectedVersionKey(versionOptions?.[0]?.key || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, session, supplierName, mode, activeSupplierId]);

  useEffect(() => {
    if (!isOpen) return;
    const hasSelectedVersion = versionOptions.some((option) => option.key === selectedVersionKey);
    if (!hasSelectedVersion) {
      setSelectedVersionKey(versionOptions?.[0]?.key || '');
    }
  }, [isOpen, selectedVersionKey, versionOptions]);

  if (!isOpen) return null;

  const handleChange = (field, value) => {
    setParams(prev => ({ ...prev, [field]: value }));
  };

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const selectedVersion = versionOptions.find((option) => option.key === selectedVersionKey) || null;
      await onGenerate(params, selectedVersion);
      if (closeOnGenerate) onClose();
    } catch (err) {
      console.error('Invoice generation failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const langOptions = [
    { value: 'pt', label: 'Português' },
    { value: 'fr', label: 'Français' },
    { value: 'en', label: 'English' },
  ];

  const showSupplierTabs =
    mode === 'single' &&
    Array.isArray(suppliers) &&
    suppliers.length > 1 &&
    typeof onSupplierChange === 'function';
  const showVersionSelector = mode === 'single' && Array.isArray(versionOptions) && versionOptions.length > 1;

  const title = mode === 'all'
    ? t('invoice.modal.titleAll')
    : t('invoice.modal.titleSingle', { supplier: supplierName });

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog-container invoice-modal-container" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="dialog-header">
          <div className="dialog-title">
            <FileText size={22} />
            <h2>{title}</h2>
          </div>
          <button className="dialog-close" onClick={onClose}><X size={20} /></button>
        </div>

        {/* Body */}
        <div className="dialog-form">

          {showSupplierTabs && (
            <div className="invoice-supplier-tabs" role="tablist">
              {suppliers.map((s) => (
                <button
                  key={s.supplier_id}
                  type="button"
                  role="tab"
                  className={`invoice-supplier-tab ${String(activeSupplierId) === String(s.supplier_id) ? 'active' : ''}`}
                  onClick={() => onSupplierChange(s.supplier_id, s.supplier_name)}
                >
                  {s.supplier_name}
                </button>
              ))}
            </div>
          )}

          {showVersionSelector && (
            <div className="invoice-version-section">
              <label className="invoice-version-label">
                <FileText size={16} />
                {t('invoice.modal.version')}
              </label>
              <div className="invoice-version-options">
                {versionOptions.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    className={`invoice-version-btn ${selectedVersionKey === option.key ? 'active' : ''}`}
                    onClick={() => setSelectedVersionKey(option.key)}
                  >
                    <span>{option.label}</span>
                    {option.meta && <small>{option.meta}</small>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Language selector */}
          <div className="invoice-lang-section">
            <label className="invoice-lang-label">
              <Globe size={16} />
              {t('invoice.modal.language')}
            </label>
            <div className="invoice-lang-options">
              {langOptions.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  className={`invoice-lang-btn ${params.lang === opt.value ? 'active' : ''}`}
                  onClick={() => handleChange('lang', opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Editable fields */}
          <div className="invoice-section-title">{t('invoice.modal.documentInfo')}</div>
          <div className="form-row">
            <div className="form-group">
              <label>{t('invoice.modal.documentNumber')}</label>
              <input
                type="text"
                value={params.document_number}
                onChange={e => handleChange('document_number', e.target.value)}
                placeholder={t('invoice.modal.documentNumberPlaceholder')}
              />
            </div>
            <div className="form-group">
              <label>{t('invoice.modal.requisitionNumber')}</label>
              <input
                type="text"
                value={params.requisition_number}
                onChange={e => handleChange('requisition_number', e.target.value)}
                placeholder={t('invoice.modal.requisitionNumberPlaceholder')}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>{t('invoice.modal.issueDate')}</label>
              <input
                type="text"
                value={params.issue_date}
                onChange={e => handleChange('issue_date', e.target.value)}
                placeholder="dd/mm/yyyy"
              />
            </div>
            <div className="form-group">
              <label>{t('invoice.modal.serviceType')}</label>
              <input
                type="text"
                value={params.service_type}
                onChange={e => handleChange('service_type', e.target.value)}
                placeholder={t('invoice.modal.serviceTypePlaceholder')}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>{t('invoice.modal.revisionNumber')}</label>
              <input
                type="text"
                value={params.revision_number}
                onChange={e => handleChange('revision_number', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>{t('invoice.modal.revisionDate')}</label>
              <input
                type="text"
                value={params.revision_date}
                onChange={e => handleChange('revision_date', e.target.value)}
                placeholder="dd/mm/yyyy"
              />
            </div>
          </div>

          <div className="invoice-section-title">{t('invoice.modal.signatories')}</div>
          <div className="form-row">
            <div className="form-group">
              <label>{t('invoice.modal.preparedBy')}</label>
              <input
                type="text"
                value={params.prepared_by}
                onChange={e => handleChange('prepared_by', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>{t('invoice.modal.revisedBy')}</label>
              <input
                type="text"
                value={params.revised_by}
                onChange={e => handleChange('revised_by', e.target.value)}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>{t('invoice.modal.approvedBy')}</label>
              <input
                type="text"
                value={params.approved_by}
                onChange={e => handleChange('approved_by', e.target.value)}
              />
            </div>
            <div className="form-group" />
          </div>

          {/* Actions */}
          <div className="dialog-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleGenerate}
              disabled={loading}
            >
              <Download size={16} />
              {loading ? t('invoice.modal.generating') : t('invoice.modal.generate')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceModal;
